import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useCountUp } from '@/hooks/useCountUp'
import { cn, formatTabCount } from '@/lib/utils'
import type * as React from 'react'
import { useRef } from 'react'

export interface TabCountBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
	count?: number | null
	active?: boolean
	showCount?: boolean
	/** Counts that load separately from the rows: spin rather than show a stale or zero number. */
	loading?: boolean
	tooltipSide?: 'top' | 'right' | 'bottom' | 'left'
	/** Enable count-up animation (defaults to true) */
	animate?: boolean
	/** Animate the first reveal only, so a polled count does not count up again on every refresh. */
	animateOnce?: boolean
	/** Animation duration in ms (default: 600ms) */
	duration?: number
	'data-testid'?: string
}

export function TabCountBadge({
	count,
	active,
	showCount = true,
	loading = false,
	className,
	tooltipSide = 'bottom',
	animate,
	animateOnce = false,
	duration = 600,
	'data-testid': testId,
	...props
}: TabCountBadgeProps) {
	if (!showCount) {
		return null
	}

	const finalCount = count ?? 0
	// `animate` is passed through untouched, so the hook's own defaults (off in tests and under reduced
	// motion) still apply once the reveal is done.
	const hasAnimatedRef = useRef(false)
	const animatedValue = useCountUp(finalCount, {
		duration,
		enabled: animateOnce && hasAnimatedRef.current ? false : animate,
		onEnd: () => {
			hasAnimatedRef.current = true
		},
	})
	const isAnimating = animatedValue !== finalCount
	const formattedCount = formatTabCount(animatedValue, !isAnimating)

	const badge = (
		<span
			data-testid={testId}
			className={cn(
				'ml-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 whitespace-nowrap tabular-nums transition-colors duration-300 ease-in-out text-control!',
				active
					? 'bg-header-tab-active-counter-bg text-header-tab-active-counter-text'
					: 'bg-header-tab-inactive-counter-bg text-header-tab-inactive-counter-text group-hover:text-header-tab-active-counter-text',
				className,
			)}
			{...props}>
			{loading ? (
				<Spinner className='size-2.5' data-testid={testId ? `${testId}-spinner` : undefined} />
			) : (
				formattedCount
			)}
		</span>
	)

	if (!loading && finalCount > 999) {
		return (
			<Tooltip delayDuration={200}>
				<TooltipTrigger asChild>{badge}</TooltipTrigger>
				<TooltipContent side={tooltipSide} className='font-normal'>
					{finalCount.toLocaleString()}
				</TooltipContent>
			</Tooltip>
		)
	}

	return badge
}
